import CopyText from "./copy_text_controller";
import { get } from "@rails/request.js"

export default class extends CopyText {
  static targets = [
    "radioButton", "configSection", "selectedType", "embedCode", "typeCard",
    "previewContainer", "previewPlaceholder",
    "cardSettings", "cardStyle", "cardBackgroundColor", "cardBackgroundColorText",
    "cardTextColor", "cardTextColorText", "cardAccentColor", "cardAccentColorText",
    "cardBorderRadius", "showCardImage", "showCardDescription", "showCardLocation",
    "showCardPrice", "cardButtonText", "cardPreview",
    "eventsCarouselSettings", "carouselBackgroundColor", "carouselBackgroundColorText",
    "carouselTextColor", "carouselTextColorText", "carouselAccentColor", "carouselAccentColorText",
    "carouselBorderRadius", "autoplay", "showCarouselNavigation", "showCarouselIndicators",
    "showCarouselDate", "showCarouselPrice", "slideInterval", "carouselButtonText", "eventsCarouselPreview"
  ]

  static values = {
    organizationId: String,
    copiedSuccess: String,
  }

  connect() {
    this.initializeDefaults()
  }

  initializeDefaults() {
    this.currentWidgetType = 'events-card'
    this.selectedTypeTarget.innerHTML = '<p class="widget-type-tag">Widget Type: <span>Events Cards</span></p>'

    // Hide carousel settings initially
    if (this.hasEventsCarouselSettingsTarget) {
      this.eventsCarouselSettingsTarget.style.display = 'none'
    }

    // Show card settings by default
    if (this.hasCardSettingsTarget) {
      this.cardSettingsTarget.style.display = 'block'
    }

    this.updateCardPreview()
  }

  selectWidget(event) {
    const radio = event.currentTarget
    const widgetType = radio.value

    if (radio.checked) {
      this.currentWidgetType = widgetType

      // Remove active class from all cards
      this.typeCardTargets.forEach(card => {
        card.classList.remove('active')
      })

      // Add active class to selected card
      radio.closest('.type-selection-card').classList.add('active')

      // Show configuration section
      this.configSectionTarget.style.display = 'block'

      // Update selected type display
      const typeNames = {
        'events-card': 'Events Cards',
        'events-carousel': 'Events Carousel'
      }

      this.selectedTypeTarget.innerHTML = `<p class="widget-type-tag">Widget Type: <span>${typeNames[widgetType]}</span></p>`

      // Hide all settings
      if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'none'
      if (this.hasEventsCarouselSettingsTarget) this.eventsCarouselSettingsTarget.style.display = 'none'

      // Show/hide settings based on widget type
      if (widgetType === 'events-card') {
        if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'block'
        this.updateCardPreview()
      } else if (widgetType === 'events-carousel') {
        if (this.hasEventsCarouselSettingsTarget) this.eventsCarouselSettingsTarget.style.display = 'block'
        this.updateCarouselPreview()
      }

      // Update embed code
      if (widgetType === 'events-card') {
        this.updateCardEmbedCode()
      } else if (widgetType === 'events-carousel') {
        this.updateCarouselEmbedCode()
      }
    }
  }

  selectCard(event) {
    // If clicking on the card but not the radio button, select the radio
    if (event.target.type !== 'radio') {
      const radio = event.currentTarget.querySelector('input[type="radio"]')
      if (radio) {
        radio.checked = true
        // Trigger the change event
        this.selectWidget({ currentTarget: radio })
      }
    }
  }

  updateCardPreview() {
    const params = this.getCardParams()

    const queryParams = new URLSearchParams({ ...params, widget_type: 'events-card' })
    const url = `/organizer/organizations/${this.organizationIdValue}/promote/preview?${queryParams}`

    // Hide other previews
    if (this.hasEventsCarouselPreviewTarget) {
      this.eventsCarouselPreviewTarget.style.display = 'none'
    }
    if (this.hasPreviewPlaceholderTarget) {
      this.previewPlaceholderTarget.style.display = 'none'
    }

    get(url, { responseKind: 'json' })
      .then(async response => {
        const data = await response.json
        if (data.status === 'success' && this.hasCardPreviewTarget) {
          this.cardPreviewTarget.innerHTML = data.html
          this.cardPreviewTarget.style.display = 'block'
        }
      })
      .catch(error => {
        console.error('Error updating events card preview:', error)
        if (this.hasCardPreviewTarget) {
          this.cardPreviewTarget.innerHTML = '<div class="alert alert-danger">Error loading preview</div>'
        }
      })

    this.updateCardEmbedCode()
  }

  getCardParams() {
    return {
      card_style: this.hasCardStyleTarget ? this.cardStyleTarget.value : 'compact',
      card_background_color: this.hasCardBackgroundColorTarget ? this.cardBackgroundColorTarget.value : '#ffffff',
      card_text_color: this.hasCardTextColorTarget ? this.cardTextColorTarget.value : '#333333',
      card_accent_color: this.hasCardAccentColorTarget ? this.cardAccentColorTarget.value : '#007bff',
      card_border_radius: this.hasCardBorderRadiusTarget ? this.cardBorderRadiusTarget.value : '12',
      show_card_image: this.hasShowCardImageTarget ? this.showCardImageTarget.checked : true,
      show_card_description: this.hasShowCardDescriptionTarget ? this.showCardDescriptionTarget.checked : true,
      show_card_location: this.hasShowCardLocationTarget ? this.showCardLocationTarget.checked : true,
      show_card_price: this.hasShowCardPriceTarget ? this.showCardPriceTarget.checked : true,
      card_button_text: this.hasCardButtonTextTarget ? this.cardButtonTextTarget.value : 'Get Tickets'
    }
  }

  updateCardEmbedCode() {
    const params = this.getCardParams()
    const queryParams = new URLSearchParams({ ...params, widget_type: 'events-card' })
    const url = `/organizer/organizations/${this.organizationIdValue}/promote/embed_code?${queryParams}`

    get(url, { responseKind: 'json' })
      .then(async response => {
        const data = await response.json
        if (data.status === 'success' && this.hasEmbedCodeTarget) {
          this.embedCodeTarget.textContent = data.embed_code
        }
      })
      .catch(error => console.error('Error updating embed code:', error))
  }

  // Color sync methods
  updateCardBackgroundColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardBackgroundColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardBackgroundColorTarget.value = color
      this.updateCardPreview()
    }
  }

  updateCardTextColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardTextColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardTextColorTarget.value = color
      this.updateCardPreview()
    }
  }

  updateCardAccentColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardAccentColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardAccentColorTarget.value = color
      this.updateCardPreview()
    }
  }

  // Update color text when color picker changes
  syncCardBackgroundColorText() {
    if (this.hasCardBackgroundColorTarget && this.hasCardBackgroundColorTextTarget) {
      this.cardBackgroundColorTextTarget.value = this.cardBackgroundColorTarget.value
    }
  }

  syncCardTextColorText() {
    if (this.hasCardTextColorTarget && this.hasCardTextColorTextTarget) {
      this.cardTextColorTextTarget.value = this.cardTextColorTarget.value
    }
  }

  syncCardAccentColorText() {
    if (this.hasCardAccentColorTarget && this.hasCardAccentColorTextTarget) {
      this.cardAccentColorTextTarget.value = this.cardAccentColorTarget.value
    }
  }

  // Utility methods
  copyEmbedCode(event) {
    const code = this.embedCodeTarget.textContent
    if (this.copyToClipboard(code)) {
      displayFlashMessage('success', this.copiedSuccessValue)
    }
  }

  isValidHex(hex) {
    return /^#([0-9A-F]{3}){1,2}$/i.test(hex)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Carousel Methods
  updateCarouselPreview() {
    const params = this.getCarouselParams()

    const queryParams = new URLSearchParams({ ...params, widget_type: 'events-carousel' })
    const url = `/organizer/organizations/${this.organizationIdValue}/promote/preview?${queryParams}`

    // Hide other previews
    if (this.hasCardPreviewTarget) {
      this.cardPreviewTarget.style.display = 'none'
    }
    if (this.hasPreviewPlaceholderTarget) {
      this.previewPlaceholderTarget.style.display = 'none'
    }

    get(url, { responseKind: 'json' })
      .then(async response => {
        const data = await response.json
        if (data.status === 'success' && this.hasEventsCarouselPreviewTarget) {
          // Extract HTML and script separately
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = data.html
          
          // Remove any existing script tags (they won't execute via innerHTML)
          const scripts = tempDiv.querySelectorAll('script')
          scripts.forEach(script => script.remove())
          
          // Inject the HTML
          this.eventsCarouselPreviewTarget.innerHTML = tempDiv.innerHTML
          this.eventsCarouselPreviewTarget.style.display = 'block'
          
          // Initialize slick-carousel after HTML is injected
          // Use setTimeout to ensure DOM is updated
          setTimeout(() => {
            this.initializeCarouselPreview(params)
          }, 50)
        }
      })
      .catch(error => {
        console.error('Error updating carousel preview:', error)
        if (this.hasEventsCarouselPreviewTarget) {
          this.eventsCarouselPreviewTarget.innerHTML = '<div class="alert alert-danger">Error loading carousel preview</div>'
        }
      })

    this.updateCarouselEmbedCode()
  }

  initializeCarouselPreview(params) {
    // Wait for jQuery and slick to be available
    const initSlick = () => {
      if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick !== 'undefined') {
        // Find the carousel container in the preview
        const carouselContainer = this.eventsCarouselPreviewTarget.querySelector('.widget-carousel-container, [id^="preview-carousel-"]')
        
        if (carouselContainer) {
          const $carousel = jQuery(carouselContainer)
          
          // Destroy existing slick instance if it exists
          if ($carousel.hasClass('slick-initialized')) {
            $carousel.slick('unslick')
          }
          
          // Get config from data attribute if available, otherwise use params
          let carouselConfig = {}
          try {
            const dataConfig = carouselContainer.getAttribute('data-carousel-config')
            if (dataConfig) {
              carouselConfig = JSON.parse(dataConfig)
            }
          } catch (e) {
            console.warn('Could not parse carousel config from data attribute', e)
          }
          
          // Initialize slick-carousel
          const slickConfig = {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: true,
            fade: true,
            cssEase: 'linear',
            autoplay: carouselConfig.autoplay !== false && (params.autoplay !== false && params.autoplay !== 'false'),
            autoplaySpeed: (carouselConfig.slide_interval || params.slide_interval || 5) * 1000,
            arrows: carouselConfig.show_navigation !== false && (params.show_carousel_navigation !== false && params.show_carousel_navigation !== 'false'),
            dots: carouselConfig.show_indicators !== false && (params.show_carousel_indicators !== false && params.show_carousel_indicators !== 'false'),
            adaptiveHeight: false,
            pauseOnHover: true,
            pauseOnFocus: true,
            swipe: true,
            touchMove: true
          }
          
          $carousel.slick(slickConfig)
        }
      } else {
        // Retry after a short delay
        setTimeout(initSlick, 100)
      }
    }
    
    // Start initialization immediately (DOM is already ready)
    initSlick()
  }

  getCarouselParams() {
    return {
      carousel_background_color: this.hasCarouselBackgroundColorTarget ? this.carouselBackgroundColorTarget.value : '#ffffff',
      carousel_text_color: this.hasCarouselTextColorTarget ? this.carouselTextColorTarget.value : '#ffffff',
      carousel_accent_color: this.hasCarouselAccentColorTarget ? this.carouselAccentColorTarget.value : '#007bff',
      carousel_border_radius: this.hasCarouselBorderRadiusTarget ? this.carouselBorderRadiusTarget.value : '12',
      autoplay: this.hasAutoplayTarget ? this.autoplayTarget.checked : true,
      show_carousel_navigation: this.hasShowCarouselNavigationTarget ? this.showCarouselNavigationTarget.checked : true,
      show_carousel_indicators: this.hasShowCarouselIndicatorsTarget ? this.showCarouselIndicatorsTarget.checked : true,
      show_carousel_date: this.hasShowCarouselDateTarget ? this.showCarouselDateTarget.checked : true,
      show_carousel_price: this.hasShowCarouselPriceTarget ? this.showCarouselPriceTarget.checked : true,
      slide_interval: this.hasSlideIntervalTarget ? this.slideIntervalTarget.value : '5',
      carousel_button_text: this.hasCarouselButtonTextTarget ? this.carouselButtonTextTarget.value : 'Get Tickets'
    }
  }

  updateCarouselEmbedCode() {
    const params = this.getCarouselParams()
    const queryParams = new URLSearchParams({ ...params, widget_type: 'events-carousel' })
    const url = `/organizer/organizations/${this.organizationIdValue}/promote/embed_code?${queryParams}`

    get(url, { responseKind: 'json' })
      .then(async response => {
        const data = await response.json
        if (data.status === 'success' && this.hasEmbedCodeTarget) {
          this.embedCodeTarget.textContent = data.embed_code
        }
      })
      .catch(error => console.error('Error updating carousel embed code:', error))
  }

  // Carousel color sync methods
  updateCarouselBackgroundColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCarouselBackgroundColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.carouselBackgroundColorTarget.value = color
      this.updateCarouselPreview()
    }
  }

  updateCarouselTextColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCarouselTextColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.carouselTextColorTarget.value = color
      this.updateCarouselPreview()
    }
  }

  updateCarouselAccentColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCarouselAccentColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.carouselAccentColorTarget.value = color
      this.updateCarouselPreview()
    }
  }

  // Carousel color sync methods
  syncCarouselBackgroundColorText() {
    if (this.hasCarouselBackgroundColorTarget && this.hasCarouselBackgroundColorTextTarget) {
      this.carouselBackgroundColorTextTarget.value = this.carouselBackgroundColorTarget.value
    }
  }

  syncCarouselTextColorText() {
    if (this.hasCarouselTextColorTarget && this.hasCarouselTextColorTextTarget) {
      this.carouselTextColorTextTarget.value = this.carouselTextColorTarget.value
    }
  }

  syncCarouselAccentColorText() {
    if (this.hasCarouselAccentColorTarget && this.hasCarouselAccentColorTextTarget) {
      this.carouselAccentColorTextTarget.value = this.carouselAccentColorTarget.value
    }
  }
}
